import React, { useContext } from 'react';
import c from 'classnames';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import { PopupContext } from '../../../popups/PopupContext';
import ModalBody from '../../base/ModalBody';
import ModalFormBody, { ModalFormBodyProps } from '../../base/ModalFormBody';
import s from './HeaderlessLayout.scss';

export interface Props {
  isFullscreen?: boolean;

  /**
   * Displayed in the middle of the modal. We don't have standard content spacing defined, so you're on your own with
   * regards to that.
   */
  content: React.ReactNode;

  /**
   * A `className` that gets passed to the root element.
   */
  className?: string;

  /**
   * A `className` for the content of the modal.
   */
  contentClassName?: string;

  /**
   * Ref of node with the overflow on it
   */
  innerScrollRef?: (node) => void;

  /** Wrap content in a scrollable div. Default is true */
  withScrollableContentWrapper?: boolean;

  /**
   * Will wrap the body contents in a form element and add this submit handler to it. Useful when the whole modal should
   * be treated as a form.
   */
  onSubmit?: ModalFormBodyProps['onSubmit'];
}

/**
 * A lobotomized version of dsPresets/ModalLayout (see note in HeaderlessModal for reasons)
 */
const HeaderlessLayout: React.FC<Props> = ({
  isFullscreen,
  content,
  className,
  contentClassName,
  innerScrollRef,
  onSubmit,
  withScrollableContentWrapper = true,
}) => {
  const { setDefaultScrollableRef } = useContext(PopupContext);

  const children = (
    <>
      <div
        className={c(
          s['content'],
          withScrollableContentWrapper && s['scrollable'],
          contentClassName
        )}
        ref={node => {
          if (withScrollableContentWrapper) {
            innerScrollRef && innerScrollRef(node);
            // Set the default scrollable, see ModalRoot for the logic
            setDefaultScrollableRef && setDefaultScrollableRef(node);
          }
        }}
      >
        {content}
      </div>
    </>
  );

  return onSubmit ? (
    <ModalFormBody
      className={c(s['root'], isFullscreen && s['fullscreen'], className)}
      surfaceType={ThemeSurface.TERTIARY}
      isFullscreen={isFullscreen}
      onSubmit={onSubmit}
    >
      {children}
    </ModalFormBody>
  ) : (
    <ModalBody
      className={c(s['root'], isFullscreen && s['fullscreen'], className)}
      surfaceType={ThemeSurface.TERTIARY}
      isFullscreen={isFullscreen}
    >
      {children}
    </ModalBody>
  );
};

HeaderlessLayout.displayName = 'HeaderlessLayout';
export default HeaderlessLayout;
